import { apiClient } from './api';
import { Review, ReviewStatus, ReviewsQueryParams, ReviewPageParams, PagedResponse } from '../types/review';

/** Default number of reviews returned per page. */
export const REVIEWS_DEFAULT_PAGE_SIZE = 20;

/**
 * The backend returns status values in UPPERCASE (e.g. "PENDING").
 * Normalise to lowercase so all frontend comparisons use the ReviewStatus union.
 */
const normalizeReview = (raw: Review & { status: string }): Review => ({
  ...raw,
  status: raw.status.toLowerCase() as ReviewStatus,
});

export const reviewService = {
  /**
   * GET /api/v1/reviews?status=&apartmentId=&page=&size=
   * Returns a paginated list of reviews filtered by status and/or apartment.
   * The status query param is sent uppercase to match the backend enum.
   * No withConflictRetry — read-only endpoint (constitution §III-E).
   */
  async getReviews(
    params: ReviewsQueryParams = {},
    pageParams: ReviewPageParams = {},
  ): Promise<PagedResponse<Review>> {
    const apiParams = {
      ...(params.status ? { status: params.status.toUpperCase() } : {}),
      ...(params.apartmentId ? { apartmentId: params.apartmentId } : {}),
      page: pageParams.page ?? 0,
      size: pageParams.size ?? REVIEWS_DEFAULT_PAGE_SIZE,
    };
    const response = await apiClient.get<PagedResponse<Review & { status: string }>>(
      '/reviews',
      { params: apiParams },
    );
    return {
      ...response.data,
      content: response.data.content.map(normalizeReview),
    };
  },

  /**
   * POST /api/v1/reviews/{id}/publish
   * Approves a pending review. No request body.
   * withConflictRetry is intentionally NOT used — no optimistic lock on this endpoint.
   */
  async approveReview(id: string): Promise<Review> {
    const response = await apiClient.post<Review>(`/reviews/${id}/publish`);
    return normalizeReview(response.data);
  },

  /**
   * POST /api/v1/reviews/{id}/reject
   * Rejects a pending review. No request body.
   * withConflictRetry is intentionally NOT used — no optimistic lock on this endpoint.
   */
  async rejectReview(id: string): Promise<Review> {
    const response = await apiClient.post<Review>(`/reviews/${id}/reject`);
    return normalizeReview(response.data);
  },
};
